import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/utils/cn';

import { IconButton } from './IconButton';

type Size = 'sm' | 'md' | 'lg';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Drawer width. sm ≈ 360px, md ≈ 460px, lg ≈ 600px. Defaults to md. */
  size?: Size;
  children: ReactNode;
  footer?: ReactNode;
  closeOnScrim?: boolean;
  bodyClassName?: string;
  ariaLabel?: string;
}

/**
 * Right-side slide-in panel. Shares the modal's portal + focus-trap + ESC +
 * scrim behaviour (see Modal.tsx) but anchors a full-height panel to the right
 * edge. Use for editors that benefit from keeping the page visible behind them,
 * e.g. the contacts filter builder.
 */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  size = 'md',
  children,
  footer,
  closeOnScrim = true,
  bodyClassName,
  ariaLabel,
}: DrawerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const lastActiveRef = useRef<Element | null>(null);
  // onClose changes identity on every parent render — read it through a ref so
  // the open-effect deps stay stable and focus isn't yanked on every keystroke.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    lastActiveRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      } else if (e.key === 'Tab') {
        const root = ref.current;
        if (!root) return;
        const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
          (el) => el.offsetParent !== null,
        );
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);

    queueMicrotask(() => {
      const root = ref.current;
      if (!root) return;
      const first = root.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus();
    });

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (lastActiveRef.current instanceof HTMLElement) {
        lastActiveRef.current.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  const sizeClass = size === 'sm' ? 'size-sm' : size === 'lg' ? 'size-lg' : '';

  return createPortal(
    <div
      className="ds-scrim ds-scrim-right"
      onMouseDown={(e) => {
        if (closeOnScrim && e.target === e.currentTarget) onCloseRef.current();
      }}
    >
      <div
        ref={ref}
        className={cn('ds-drawer', sizeClass)}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : ariaLabel}
      >
        <div className="ds-drawer-head">
          {title && <h2>{title}</h2>}
          {subtitle && <span className="ds-drawer-sub">{subtitle}</span>}
          <IconButton icon="x" label="Close" className="ds-drawer-close" onClick={onClose} />
        </div>
        <div className={cn('ds-drawer-body', bodyClassName)}>{children}</div>
        {footer && <div className="ds-drawer-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
